import { useEffect, useState } from 'react';
import { Flex, Heading, Box, Stack, Button, Icon } from '@chakra-ui/react';
import { PrinterIcon } from '@heroicons/react/24/outline';
import { NavLink, useParams } from 'react-router-dom';
import PrintButton from '@/components/ui/print-button';
import { LoginButton } from '@/components/ui/login-button';
import PrintFieldSelector from '@/components/ui/print-field-selector';
import Carousel from '@/components/carousel';
import BlockContentType from '@/components/content/block-content-type';
import CommentSection from '@/components/comments/comment-section';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useIsUser from '@/lib/hooks/use-is-user';
import { Content, ContentTypeFieldView, File, FileType, BlockType, StaticField } from '@/lib/types';

import '@/styles/print.css';

interface Props {
  data: {
    content: Content;
    subContents: any;
    contentTypeFields: ContentTypeFieldView[];
    workspaceCreatorEmail: string;
    workspaceName: string;
  };
  isPublic?: boolean;
}

interface FieldInfo {
  key: string;
  type: BlockType;
  label: string;
  options: Map<string, string>;
}

const getFileType = (fileUrl: string): FileType.Image | FileType.Video => {
  const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'];
  const extension = fileUrl.split('.').pop()?.toLowerCase();

  if (extension && videoExtensions.includes(extension)) {
    return FileType.Video;
  }
  return FileType.Image;
};

const createFieldInfo = (
  field: ContentTypeFieldView,
  includeOptions: boolean = false,
): FieldInfo => {
  let options = new Map<string, string>();

  if (includeOptions && field.field_type === BlockType.Selection) {
    if (field.field_options && field.field_options.length > 0) {
      options = field.field_options.reduce((map, item) => {
        map.set(item.option_value, item.option_label);
        return map;
      }, new Map<string, string>());
    }
  }

  return {
    key: field.referenceID,
    type: field.field_type,
    label: field.field_name,
    options: options,
  };
};

export default function ViewContent({ data, isPublic = false }: Props) {
  const { workspaceId } = useParams<{
    workspaceId: string;
  }>();
  const [files, setFiles] = useState<File[]>([]);
  const [fields, setFields] = useState<FieldInfo[]>([]);
  const [printableFields, setPrintableFields] = useState<string[]>([]);
  const isAdmin = useIsInAdminGroup();
  const isUser = useIsUser();

  useEffect(() => {
    if (!data?.content?.media_files?.files) {
      setFiles([]);
      return;
    }

    const mediaFiles = data.content.media_files.files;
    const processedFiles: File[] = mediaFiles.map((file: string) => ({
      type: getFileType(file),
      src: file,
    }));

    setFiles(processedFiles);
  }, [data.content.media_files]);

  useEffect(() => {
    if (!data?.content?.content || !data?.contentTypeFields) {
      setFields([]);
      return;
    }

    const content = { ...data.content };
    const contentTypeFields = [...(data.contentTypeFields || [])];

    if (content.description) {
      content.content[StaticField.Description] = {
        value: content.description,
      };
    }

    const contentRefIds = new Set(Object.keys(content.content || {}));

    const sortedFields = [...contentTypeFields].sort((a, b) => a.sort_order - b.sort_order);

    const processedFields = sortedFields.reduce<FieldInfo[]>((result, field) => {
      if (field.field_type === BlockType.SectionTitle) {
        result.push(createFieldInfo(field));
        return result;
      }

      if (field.field_unique_name === StaticField.Description) {
        field.referenceID = field.field_unique_name;
      }

      if (field.referenceID && contentRefIds.has(field.referenceID)) {
        result.push(createFieldInfo(field, true));
      }

      return result;
    }, []);

    setFields(processedFields);
  }, [data]);

  const handlePrintWithFields = (selectedFields: string[]) => {
    setPrintableFields(selectedFields);

    setTimeout(() => {
      window.print();
    }, 300);
  };

  const fieldOptions = fields.map((field) => ({
    id: field.key,
    label: field.label,
    type: BlockType[field.type] || 'Other',
  }));

  if (files.length > 0) {
    fieldOptions.unshift({
      id: 'media-files',
      label: 'Images & Videos',
      type: 'Media',
    });
  }

  const shouldPrintField = (fieldId: string): boolean => {
    return printableFields.length === 0 || printableFields.includes(fieldId);
  };

  const { content, subContents } = data || { content: {}, subContents: {} };

  const isDisplayComment =
    (isAdmin || isUser) && workspaceId && parseInt(workspaceId, 10) === content?.workspace_id;

  return (
    <Box pb={6} className='print-container'>
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justifyContent='space-between'
        alignItems='center'
        mb={6}
        mt={2}
        gap={6}
      >
        <Heading as={'h1'} size={'2xl'}>
          {content?.name}
        </Heading>
        {isPublic ? (
          <Flex gap={3}>
            <PrintFieldSelector
              fields={fieldOptions}
              onPrint={handlePrintWithFields}
              buttonText='Custom Print'
            />
            <PrintButton documentTitle={content?.name} />
            <LoginButton />
          </Flex>
        ) : (
          <Button colorPalette='brand' size='sm' mr={6} asChild>
            <NavLink to={`/public/content/${content.reference_id}`} target='_blank'>
              Print
              <Icon as={PrinterIcon} w={4} h={4} />
            </NavLink>
          </Button>
        )}
      </Flex>

      <Stack gap={4} px={2}>
        {files.length > 0 && (
          <Box className={`media-container ${!shouldPrintField('media-files') ? 'no-print' : ''}`}>
            <Carousel items={files} />
          </Box>
        )}

        {fields.map((field) => (
          <Box
            key={field.key}
            className={`field-container ${!shouldPrintField(field.key) ? 'no-print' : 'print-field'}`}
            mb={4}
          >
            <BlockContentType
              key={field.key}
              block={field}
              content={content?.content?.[field.key]?.value || ''}
              subContents={subContents}
              isPublic={isPublic}
            />
          </Box>
        ))}

        {isDisplayComment && !isPublic && (
          <Box className='no-print'>
            <CommentSection contentId={content?.id} />
          </Box>
        )}
      </Stack>
    </Box>
  );
}
